/* eslint-disable max-lines */
import {
  AnchorProvider,
  Program,
  Provider,
  setProvider,
  type IdlAccounts,
  type ProgramAccount
} from '@coral-xyz/anchor'
import type { WalletContextState } from '@solana/wallet-adapter-react'
import { connection, walletAdapterToAnchorWallet } from './connection'
import { expandInstructionEncodeBuffer } from './expand-ix-coder'
import type { Bullseye } from './idl/bullseye'
import BullseyeIDL from './idl/bullseye.json'
import type { OneVsOne } from './idl/one_vs_one'
import OneVsOneIDL from './idl/one_vs_one.json'
import type { Race } from './idl/race'
import RaceIDL from './idl/race.json'
import type { Setup } from './idl/setup'
import SetupIDL from './idl/setup.json'
import type { Updown } from './idl/updown'
import UpdownIDL from './idl/updown.json'
import UpdownAiIDL from './idl/updown_ai.json'
import type { XyroTreasury } from './idl/xyro_treasury'
import TreasuryIDL from './idl/xyro_treasury.json'
import type { UpdownAi } from './idl/updown_ai'
import { withProgramAddress } from './with-program-address'

const providerOpts = {
  commitment: 'confirmed' as const,
  preflightCommitment: 'processed' as const
}

export type UpdownGameAccountData = IdlAccounts<Updown>['game']
export type UpdownGameAccount = ProgramAccount<UpdownGameAccountData>

export const UpdownGameStatus = {
  Idle: 0,
  Open: 1,
  Started: 2,
  FinalizedUp: 3,
  FinalizedDown: 4,
  Cancelled: 5,
  Paid: 6
}

export function createUpdownClient(wallet: WalletContextState): {
  provider: Provider
  updownProgram: Program<Updown>
} {
  const anchorWallet = walletAdapterToAnchorWallet(wallet)
  const provider = new AnchorProvider(connection, anchorWallet, providerOpts)
  setProvider(provider)
  const updownProgram = new Program(
    UpdownIDL as never,
    provider
  ) as unknown as Program<Updown>
  return { provider, updownProgram }
}

export type BullseyeGameAccountData = IdlAccounts<Bullseye>['game']
export type BullseyeGameAccount = ProgramAccount<BullseyeGameAccountData>

export const BullseyeGameStatus = {
  Idle: 0,
  Open: 1,
  Finalized: 2,
  Cancelled: 3,
  Paid: 4
}

/** `programAddress` is the deployment the API names for the round; the IDL's own address is the default. */
export function createBullseyeClient(
  wallet: WalletContextState,
  programAddress = BullseyeIDL.address
): {
  provider: Provider
  bullseyeProgram: Program<Bullseye>
} {
  const anchorWallet = walletAdapterToAnchorWallet(wallet)
  const provider = new AnchorProvider(connection, anchorWallet, providerOpts)
  setProvider(provider)
  const bullseyeProgram = new Program(
    withProgramAddress(BullseyeIDL as never, programAddress),
    provider
  ) as unknown as Program<Bullseye>
  return { provider, bullseyeProgram }
}

export type TreasuryConfigAccountData = IdlAccounts<XyroTreasury>['tokenConfig']
export type TreasuryConfigAccount = ProgramAccount<TreasuryConfigAccountData>
export type PlayerDepositAccountData =
  IdlAccounts<XyroTreasury>['playerDeposit']
export type PlayerDepositAccount = ProgramAccount<PlayerDepositAccountData>

export function createTreasuryClient(wallet: WalletContextState): {
  provider: Provider
  treasuryProgram: Program<XyroTreasury>
} {
  const anchorWallet = walletAdapterToAnchorWallet(wallet)
  const provider = new AnchorProvider(connection, anchorWallet, providerOpts)
  setProvider(provider)
  const treasuryProgram = new Program(
    TreasuryIDL as never,
    provider
  ) as unknown as Program<XyroTreasury>
  return { provider, treasuryProgram }
}

export type OneVsOneGameAccountData = IdlAccounts<OneVsOne>['game']
export type OneVsOneGameAccount = ProgramAccount<OneVsOneGameAccountData>

export const OneVsOneGameStatus = {
  Default: 0,
  Created: 1,
  Cancelled: 2,
  Started: 3,
  Finished: 4
}

export function createOneVsOneClient(wallet: WalletContextState): {
  provider: Provider
  onevsoneProgram: Program<OneVsOne>
} {
  const anchorWallet = walletAdapterToAnchorWallet(wallet)
  const provider = new AnchorProvider(connection, anchorWallet, providerOpts)
  setProvider(provider)
  const onevsoneProgram = new Program(
    OneVsOneIDL as never,
    provider
  ) as unknown as Program<OneVsOne>
  return { provider, onevsoneProgram }
}

export type SetupGameAccountData = IdlAccounts<Setup>['game']
export type SetupGameAccount = ProgramAccount<SetupGameAccountData>

export function createSetupClient(wallet: WalletContextState): {
  provider: Provider
  setupProgram: Program<Setup>
} {
  const anchorWallet = walletAdapterToAnchorWallet(wallet)
  const provider = new AnchorProvider(connection, anchorWallet, providerOpts)
  setProvider(provider)
  const setupProgram = new Program(
    SetupIDL as never,
    provider
  ) as unknown as Program<Setup>
  expandInstructionEncodeBuffer(setupProgram)
  return { provider, setupProgram }
}

export type RaceGameAccountData = IdlAccounts<Race>['game']
export type RaceGameAccount = ProgramAccount<RaceGameAccountData>

export function createRaceClient(wallet: WalletContextState): {
  provider: Provider
  raceProgram: Program<Race>
} {
  const anchorWallet = walletAdapterToAnchorWallet(wallet)
  const provider = new AnchorProvider(connection, anchorWallet, providerOpts)
  setProvider(provider)
  const raceProgram = new Program(
    RaceIDL as never,
    provider
  ) as unknown as Program<Race>
  return { provider, raceProgram }
}

export type UpdownAiGameAccountData = IdlAccounts<UpdownAi>['game']
export type UpdownAiGameAccount = ProgramAccount<UpdownAiGameAccountData>

export function createUpdownAiClient(wallet: WalletContextState): {
  provider: Provider
  updownAiProgram: Program<UpdownAi>
} {
  const anchorWallet = walletAdapterToAnchorWallet(wallet)
  const provider = new AnchorProvider(connection, anchorWallet, providerOpts)
  setProvider(provider)
  const updownAiProgram = new Program(
    UpdownAiIDL as never,
    provider
  ) as unknown as Program<UpdownAi>
  expandInstructionEncodeBuffer(updownAiProgram)
  return { provider, updownAiProgram }
}
